import { useState, useEffect } from 'react';
import api from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Plus, Send, Trash2, Link2, Radio, Power, Loader2, ExternalLink, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const PLATFORMS = [
  {
    id: 'telegram', label: 'Telegram', color: '#0088CC', icon: 'T',
    description: 'Bot API ile kanal/gruplara otomatik mesaj ve görsel gönder',
    fields: [
      { key: 'channel_name', label: 'Kanal Adı', placeholder: '@kanal_adi' },
      { key: 'channel_id', label: 'Kanal ID', placeholder: '-1001234567890' },
      { key: 'bot_token', label: 'Bot Token', placeholder: '123456:ABC-DEF...' },
    ],
    status: 'active',
    helpUrl: 'https://core.telegram.org/bots#how-do-i-create-a-bot',
  },
  {
    id: 'twitter', label: 'X (Twitter)', color: '#000000', icon: 'X',
    description: 'Tweet at, medya paylaş, hashtag takibi yap',
    fields: [
      { key: 'channel_name', label: 'Hesap Adı', placeholder: '@hesap_adi' },
      { key: 'consumer_key', label: 'Consumer Key (API Key)', placeholder: 'Consumer key...' },
      { key: 'consumer_secret', label: 'Consumer Secret', placeholder: 'Consumer secret...' },
      { key: 'access_token', label: 'Access Token', placeholder: 'Access token...' },
      { key: 'access_token_secret', label: 'Access Token Secret', placeholder: 'Access token secret...' },
    ],
    status: 'active',
    helpUrl: 'https://developer.x.com/en/docs/authentication',
  },
  {
    id: 'instagram', label: 'Instagram', color: '#E4405F', icon: 'I',
    description: 'Hikaye ve gönderi paylaşımı, görsel içerik dağıtımı',
    fields: [
      { key: 'channel_name', label: 'Hesap Adı', placeholder: '@hesap_adi' },
      { key: 'channel_id', label: 'Instagram User ID', placeholder: '17841400...' },
      { key: 'access_token', label: 'Access Token', placeholder: 'Instagram Graph API token...' },
    ],
    status: 'active',
    helpUrl: 'https://developers.facebook.com/docs/instagram-api/',
  },
  {
    id: 'facebook', label: 'Facebook', color: '#1877F2', icon: 'F',
    description: 'Facebook Sayfa gönderisi paylaşımı ve görsel içerik',
    fields: [
      { key: 'channel_name', label: 'Sayfa Adı', placeholder: 'Sayfa adı' },
      { key: 'channel_id', label: 'Page ID', placeholder: '12345678...' },
      { key: 'access_token', label: 'Page Access Token', placeholder: 'Facebook Page token...' },
    ],
    status: 'active',
    helpUrl: 'https://developers.facebook.com/docs/pages-api/',
  },
  {
    id: 'youtube', label: 'YouTube', color: '#FF0000', icon: 'Y',
    description: 'YouTube kanalına topluluk gönderisi ve içerik paylaşımı',
    fields: [
      { key: 'channel_name', label: 'Kanal Adı', placeholder: '@kanal' },
      { key: 'channel_id', label: 'Channel ID', placeholder: 'UCxxxxxxxxx...' },
      { key: 'access_token', label: 'OAuth Token', placeholder: 'YouTube API OAuth token...' },
    ],
    status: 'active',
    helpUrl: 'https://developers.google.com/youtube/v3',
  },
  {
    id: 'tiktok', label: 'TikTok', color: '#010101', icon: 'Tk',
    description: 'TikTok Content API ile foto/video paylaşımı',
    fields: [
      { key: 'channel_name', label: 'Hesap Adı', placeholder: '@hesap_adi' },
      { key: 'access_token', label: 'Access Token', placeholder: 'TikTok API access token...' },
    ],
    status: 'active',
    helpUrl: 'https://developers.tiktok.com/doc/content-posting-api-get-started/',
  },
];

export default function SocialChannelsPage() {
  const [channels, setChannels] = useState([]);
  const [posts, setPosts] = useState([]);
  const [listenerStatus, setListenerStatus] = useState({ active: false, tracked_fixtures: 0 });
  const [showConnect, setShowConnect] = useState(false);
  const [showPost, setShowPost] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [form, setForm] = useState({});
  const [postForm, setPostForm] = useState({ content: '', platform: 'telegram' });
  const [connecting, setConnecting] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get('/sports/social/channels').then(r => setChannels(r.data)).catch(() => {});
    api.get('/sports/social/posts?limit=20').then(r => setPosts(r.data)).catch(() => {});
    api.get('/sports/listener/status').then(r => setListenerStatus(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    const i = setInterval(() => api.get('/sports/listener/status').then(r => setListenerStatus(r.data)).catch(() => {}), 15000);
    return () => clearInterval(i);
  }, []);

  const toggleListener = async () => {
    try {
      const endpoint = listenerStatus.active ? '/sports/listener/stop' : '/sports/listener/start';
      const r = await api.post(endpoint);
      setListenerStatus({ active: r.data.active, tracked_fixtures: listenerStatus.tracked_fixtures });
      toast.success(r.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  const openConnect = (platform) => {
    setSelectedPlatform(platform);
    const formData = { platform: platform.id };
    platform.fields.forEach(f => { formData[f.key] = ''; });
    setForm(formData);
    setShowConnect(true);
  };

  const handleConnect = async () => {
    const pf = selectedPlatform;
    if (!form.channel_name) { toast.error('Kanal adı gerekli'); return; }
    setConnecting(true);
    try {
      await api.post('/sports/social/channels/connect', form);
      toast.success(pf.label + ' kanalı bağlandı');
      setShowConnect(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bağlantı hatası'); }
    setConnecting(false);
  };

  const handleDisconnect = async (id) => {
    try {
      await api.delete('/sports/social/channels/' + id);
      toast.success('Bağlantı kaldırıldı');
      load();
    } catch { toast.error('Hata'); }
  };

  const handlePost = async () => {
    if (!postForm.content) { toast.error('İçerik gerekli'); return; }
    setSending(true);
    try {
      const r = await api.post('/sports/social/post', postForm);
      const results = r.data.results || [];
      const sent = results.filter(x => x.success && !x.queued);
      if (sent.length > 0) {
        toast.success(sent.length + ' kanala gönderildi!');
      } else {
        toast.success('Gönderi oluşturuldu');
      }
      setShowPost(false);
      setPostForm({ content: '', platform: 'telegram' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gönderi hatası'); }
    setSending(false);
  };

  const getConnectedChannels = (platformId) => channels.filter(ch => ch.platform === platformId);
  const platformInfo = (id) => PLATFORMS.find(p => p.id === id) || { label: id, color: '#737373', icon: '?' };

  const statusLabel = (s) => {
    if (s === 'sent') return { text: 'Gönderildi', cls: 'bg-green-500/10 text-green-400 border-green-500/20' };
    if (s === 'failed') return { text: 'Başarısız', cls: 'bg-red-500/10 text-red-400 border-red-500/20' };
    if (s === 'queued') return { text: 'Sırada', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' };
    return { text: 'Planlandı', cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' };
  };

  return (
    <div data-testid="social-channels-page" className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Sosyal Dağıtım</h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">Kanal bağlantıları ve gönderi yönetimi</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowPost(true)} variant="outline" className="border-[#262626] text-[#A3A3A3] hover:text-white h-9 text-sm" data-testid="quick-post-btn">
            <Send size={14} className="mr-1" /> Hızlı Gönderi
          </Button>
        </div>
      </div>

      <Card className={"border " + (listenerStatus.active ? 'bg-green-500/5 border-green-500/20' : 'bg-[#141414] border-[#262626]')} data-testid="listener-control">
        <CardContent className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={"w-10 h-10 rounded-lg flex items-center justify-center " + (listenerStatus.active ? 'bg-green-500/10' : 'bg-[#262626]')}>
              <Radio size={20} className={listenerStatus.active ? 'text-green-400 animate-pulse' : 'text-[#525252]'} />
            </div>
            <div>
              <p className="text-white font-ibm font-medium">Maç Olay Dinleyicisi</p>
              <p className="text-xs text-[#A3A3A3] font-ibm">
                {listenerStatus.active
                  ? 'Aktif - ' + listenerStatus.tracked_fixtures + ' maç takip ediliyor'
                  : 'Durduruldu - Canlı maç olayları dinlenmiyor'}
              </p>
            </div>
          </div>
          <Button onClick={toggleListener} variant={listenerStatus.active ? 'outline' : 'default'}
            className={listenerStatus.active ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'bg-green-500 text-white hover:bg-green-600'}
            data-testid="toggle-listener-btn">
            <Power size={14} className="mr-1" /> {listenerStatus.active ? 'Durdur' : 'Başlat'}
          </Button>
        </CardContent>
      </Card>

      <div>
        <h2 className="font-outfit text-lg text-white mb-3">Platformlar</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PLATFORMS.map(pf => {
            const connected = getConnectedChannels(pf.id);
            const isActive = pf.status === 'active';
            return (
              <Card key={pf.id} className={"border transition-all duration-200 " + (connected.length > 0 ? 'bg-[#141414] border-[#262626]' : 'bg-[#0A0A0A] border-[#1A1A1A]') + ' ' + (!isActive ? 'opacity-70' : 'hover:border-[#404040]')} data-testid={"platform-card-" + pf.id}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center font-outfit font-bold text-lg" style={{ background: pf.color + '20', color: pf.color }}>
                        {pf.icon}
                      </div>
                      <div>
                        <p className="text-sm text-white font-ibm font-medium">{pf.label}</p>
                        {!isActive && <Badge variant="outline" className="text-[10px] bg-[#262626] text-[#737373] border-[#404040] mt-0.5">Yakında</Badge>}
                      </div>
                    </div>
                    {connected.length > 0 ? (
                      <CheckCircle2 size={18} className="text-green-400" />
                    ) : isActive ? (
                      <div className="w-2 h-2 rounded-full bg-[#525252]" />
                    ) : null}
                  </div>

                  <p className="text-xs text-[#737373] font-ibm mb-4 leading-relaxed">{pf.description}</p>

                  {connected.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {connected.map(ch => (
                        <div key={ch.id} className="flex items-center justify-between p-2.5 bg-[#0A0A0A] rounded-lg border border-[#1A1A1A]">
                          <div className="flex items-center gap-2 min-w-0">
                            <Link2 size={12} style={{ color: pf.color }} className="shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs text-white font-ibm truncate">{ch.channel_name}</p>
                              {ch.channel_id && <p className="text-[10px] text-[#525252] font-jetbrains truncate">ID: {ch.channel_id}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {ch.is_global && <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-400 border-blue-500/20 h-5">Global</Badge>}
                            <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-400 border-green-500/20 h-5">Bağlı</Badge>
                            <Button variant="ghost" size="sm" onClick={() => handleDisconnect(ch.id)} className="h-6 w-6 p-0 text-[#525252] hover:text-red-400" data-testid={"disconnect-" + ch.id}>
                              <Trash2 size={11} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    {isActive ? (
                      <Button onClick={() => openConnect(pf)} variant="outline" size="sm" className="flex-1 border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#404040] h-8 text-xs" data-testid={"connect-" + pf.id + '-btn'}>
                        <Plus size={12} className="mr-1" /> {connected.length > 0 ? 'Yeni Kanal Ekle' : 'Bağla'}
                      </Button>
                    ) : (
                      <Button disabled variant="outline" size="sm" className="flex-1 border-[#1A1A1A] text-[#525252] h-8 text-xs cursor-not-allowed">
                        Yakında
                      </Button>
                    )}
                    {pf.helpUrl && isActive && (
                      <a href={pf.helpUrl} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#525252] hover:text-white" data-testid={"help-" + pf.id}>
                          <ExternalLink size={12} />
                        </Button>
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <Card className="bg-[#141414] border-[#262626]">
        <CardHeader>
          <CardTitle className="font-outfit text-lg text-white">Son Gönderiler</CardTitle>
        </CardHeader>
        <CardContent>
          {posts.length === 0 ? (
            <p className="text-[#737373] font-ibm text-sm py-4 text-center">Henüz gönderi yok</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#262626]">
                  <TableHead className="text-[#A3A3A3] font-ibm">Platform</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm">İçerik</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm">Durum</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm">Tarih</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map(p => {
                  const pi = platformInfo(p.platform);
                  const sl = statusLabel(p.status);
                  return (
                    <TableRow key={p.id} className="border-[#262626] hover:bg-white/5">
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize" style={{ borderColor: pi.color + '40', color: pi.color }}>
                          {pi.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-white font-ibm text-sm max-w-[300px] truncate">{p.content}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={"text-xs " + sl.cls}>{sl.text}</Badge>
                      </TableCell>
                      <TableCell className="text-[#525252] font-jetbrains text-xs">{new Date(p.created_at).toLocaleString('tr-TR')}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showConnect} onOpenChange={setShowConnect}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="font-outfit text-white flex items-center gap-2">
              {selectedPlatform && (
                <span className="w-8 h-8 rounded-lg flex items-center justify-center font-outfit font-bold text-sm" style={{ background: selectedPlatform.color + '20', color: selectedPlatform.color }}>
                  {selectedPlatform.icon}
                </span>
              )}
              {selectedPlatform?.label} Bağla
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedPlatform?.fields.map(field => (
              <div key={field.key}>
                <Label className="text-[#A3A3A3] font-ibm text-sm">{field.label}</Label>
                <Input
                  value={form[field.key] || ''}
                  onChange={e => setForm({ ...form, [field.key]: e.target.value })}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                  placeholder={field.placeholder}
                  data-testid={"field-" + field.key}
                />
              </div>
            ))}
            {selectedPlatform?.helpUrl && (
              <a href={selectedPlatform.helpUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-[#525252] hover:text-[#FACC15] font-ibm">
                <ExternalLink size={11} /> {selectedPlatform.label} API dokümantasyonu
              </a>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnect(false)} className="border-[#262626] text-[#A3A3A3]">İptal</Button>
            <Button onClick={handleConnect} disabled={connecting} data-testid="connect-btn" className="bg-white text-black hover:bg-[#E5E5E5]">
              {connecting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
              Bağla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPost} onOpenChange={setShowPost}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="font-outfit text-white">Hızlı Gönderi</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Platform</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {PLATFORMS.filter(p => p.status === 'active').map(p => (
                  <button key={p.id} onClick={() => setPostForm({ ...postForm, platform: p.id })}
                    className={"flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-ibm border transition-colors " + (
                      postForm.platform === p.id
                        ? 'border-[#FACC15] bg-[#FACC15]/10 text-white'
                        : 'border-[#262626] bg-[#0A0A0A] text-[#A3A3A3] hover:border-[#404040]'
                    )}>
                    <span className="w-5 h-5 rounded flex items-center justify-center font-outfit font-bold text-[10px]" style={{ background: p.color + '20', color: p.color }}>{p.icon}</span>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">İçerik</Label>
              <textarea
                value={postForm.content}
                onChange={e => setPostForm({ ...postForm, content: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-[#262626] text-white mt-1 rounded-md p-3 min-h-[120px] text-sm font-ibm focus:outline-none focus:border-[#404040]"
                data-testid="post-content"
                placeholder="Gönderi içeriği..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPost(false)} className="border-[#262626] text-[#A3A3A3]">İptal</Button>
            <Button onClick={handlePost} disabled={sending} data-testid="send-post-btn" className="bg-white text-black hover:bg-[#E5E5E5]">
              {sending ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
              Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
